import { lazy, Suspense, useState } from "react";
import { Link } from "react-router";
import { Badge, Button, Dialog, EmptyState, Icon, Spinner } from "@agentsaas/ui";
import type { Asset } from "@agentsaas/protocol";
import { useT } from "../app/i18n";
import { useAsset, useAssetUrl } from "../data/assets";
import { AssetThumb } from "./AssetThumb";
import { isModel, type Version } from "./model";
import { parseAssetRef } from "../rooms/model";
import { assetRefsIn } from "./refs";

const GlbViewer = lazy(() => import("./GlbViewer"));

/**
 * 资产引用（asset://<id>@<ver>）的直接预览，用在聊天、房间、邮件、任务、提问里：
 * - 图片 / 精灵图：缩略图，点开是资产详情
 * - 模型：缩略图（有的话）+「查看 3D」按钮，点开是轻量的查看器弹窗
 * - 资产不存在 / 没权限：退化成一枚显示引用文本的标签
 */

export { assetRefsIn } from "./refs";

/** 一组引用里的资产引用 + 文本里的资产引用（去重） */
export function collectAssetRefs(refs: readonly string[], ...texts: (string | null | undefined)[]): string[] {
  return [...new Set([...refs.filter((r) => parseAssetRef(r)), ...texts.flatMap((x) => (x ? assetRefsIn(x) : []))])];
}

export function AssetRefList({ pid, refs, names }: { pid: string; refs: readonly string[]; names?: Record<string, string | undefined> }) {
  if (!refs.length) return null;
  return (
    <div className="aref-list" data-testid="asset-refs">
      {refs.map((r) => (
        <AssetRefCard key={r} pid={pid} refStr={r} name={names?.[r]} />
      ))}
    </div>
  );
}

export function AssetRefCard({ pid, refStr, name }: { pid: string; refStr: string; name?: string }) {
  const { t } = useT();
  const parsed = parseAssetRef(refStr);
  const q = useAsset(parsed?.assetId);
  const [open, setOpen] = useState(false);
  if (!parsed) return null;
  if (q.isPending)
    return (
      <div className="aref aref--missing" data-testid="asset-ref" data-state="loading">
        <Spinner label={t("common.loading")} />
      </div>
    );
  const a = q.data;
  const version = a ? (a.versions.find((v) => v.version === (parsed.version ?? a.latestVersion)) ?? null) : null;
  if (!a || !version)
    return (
      <div className="aref aref--missing small muted" data-testid="asset-ref" data-state="missing" title={refStr}>
        <Icon name="image" size={14} />
        <span className="ellipsis">{name ?? refStr}</span>
      </div>
    );
  const href = `/p/${pid}/assets/${a.id}?v=${version.version}`;
  const model = isModel(a.kind, version.mediaType);
  return (
    <div className="aref" data-testid="asset-ref" data-state="ready" data-kind={a.kind} data-asset={a.id} data-version={version.version}>
      {model ? (
        <button type="button" className="aref__thumb" onClick={() => setOpen(true)} aria-label={t("asset.ref.view_3d_named", { name: a.name })} data-testid="asset-ref-thumb">
          <AssetThumb asset={a} version={version.version} hasThumb={version.hasThumb} />
        </button>
      ) : (
        <Link to={href} className="aref__thumb" aria-label={t("asset.ref.open_named", { name: a.name })} data-testid="asset-ref-thumb">
          <AssetThumb asset={a} version={version.version} hasThumb={version.hasThumb} />
        </Link>
      )}
      <span className="aref__body">
        <Link to={href} className="aref__name ellipsis small" title={refStr} data-testid="asset-ref-open">
          <strong>{a.name}</strong> <span className="muted">{t("asset.version_short", { version: version.version })}</span>
        </Link>
        {model ? (
          <span className="aref__actions">
            <Button size="sm" icon="cube" onClick={() => setOpen(true)} data-testid="asset-ref-view3d">
              {t("asset.ref.view_3d")}
            </Button>
          </span>
        ) : (
          <span className="small muted">
            <Badge>{t(`asset.kind.${a.kind}`)}</Badge>
          </span>
        )}
      </span>
      {model ? <ModelDialog pid={pid} asset={a} version={version} open={open} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

/** 轻量的 3D 查看器弹窗（手机上是底部 sheet / 全屏） */
export function ModelDialog({ pid, asset, version, open, onClose }: { pid: string; asset: Pick<Asset, "id" | "kind" | "name">; version: Version; open: boolean; onClose(): void }) {
  const { t } = useT();
  const url = useAssetUrl(open ? asset.id : undefined, version.version, "original");
  const label = `${asset.name} ${t("asset.version_short", { version: version.version })}`;
  return (
    <Dialog open={open} onClose={onClose} title={label} closeLabel={t("shell.close")} size="lg">
      <div className="model-dialog" data-testid="model-dialog">
        {url.isPending ? (
          <Spinner label={t("common.loading")} />
        ) : !url.data ? (
          <EmptyState icon="cube" title={t("asset.preview_unavailable")} />
        ) : (
          <Suspense fallback={<Spinner label={t("common.loading")} />}>
            <GlbViewer key={url.data.url} url={url.data.url} name={label} compact mediaType={version.mediaType} meta={version.model} />
          </Suspense>
        )}
        <Link to={`/p/${pid}/assets/${asset.id}?v=${version.version}`} className="small" onClick={onClose} data-testid="model-dialog-open">
          {t("asset.ref.open_asset")}
        </Link>
      </div>
    </Dialog>
  );
}
